import type {RootStore} from '../../stores/rootStore';
import {logger} from '../../utils/logger';
import {withTimeout} from '../../utils/promise';
import {queryClient} from '../queryClient';

import {derivePromotions} from './derivePromotions';
import {EMPTY_DERIVED_PROMOTIONS} from './emptyDerivedPromotions';
import {promotionsEnabled, promotionsQueryOptions} from './promotionsQueryOptions';
import {DerivedPromotions, PromotionArms, PromotionsGate} from './types';

export const promotionsGateFromRootStore = (rootStore: RootStore): PromotionsGate => ({
  loyaltyEnabled: !!rootStore.shopSelectionStore.loyaltyEnabled,
  isLoggedIn: !!rootStore.userStore.isLoggedIn,
});

const optionsForSelectedStore = (rootStore: RootStore) => {
  const selectedStore = rootStore.shopSelectionStore.selectedStore;
  return promotionsQueryOptions(
    selectedStore?.store_number,
    selectedStore?.address?.administrative_area,
  );
};

/**
 * Synchronous, cache-only. For callers that cannot await — currently
 * `linking.ts`, whose React Navigation `parse` callback must return a value
 * synchronously. Returns undefined on a cache miss; callers MUST handle that,
 * because the promotions query may not have been fetched when a cold-start
 * deep link is resolved.
 *
 * Deliberately does NOT carry `ensurePromotions`'s gift-card guard. That guard
 * exists to stop a NETWORK CALL the middleware answers with a 400; this
 * function only reads the cache and never fetches, so there is nothing to
 * suppress. For the gift card store the lookup is a guaranteed miss anyway —
 * nothing writes that key, which is precisely what the other guard enforces.
 */
export const readPromotions = (rootStore: RootStore): DerivedPromotions | undefined => {
  const arms = queryClient.getQueryData<PromotionArms>(optionsForSelectedStore(rootStore).queryKey);
  if (!arms) return undefined;
  return derivePromotions(arms, promotionsGateFromRootStore(rootStore));
};

/**
 * How long any caller of `ensurePromotions` may wait. Promotions are always
 * optional to the flow that asks for them — `cartStore.ts` uses them to offer a
 * competition entry on an order that is ALREADY PAID and whose confirmation
 * navigation is downstream of this call. `mwApi` sets no axios timeout (axios
 * default 0 = never), and the promotions arms are Airtable-backed (~8s when
 * healthy), so a degraded upstream that holds the socket open instead of
 * failing would strand the shopper on OrderProcessingScreen with no retry —
 * `_navigatedOrderId` is already latched, so every later poll early-returns.
 * A stall is not a throw, so try/catch at the call site cannot cover this.
 */
const ENSURE_PROMOTIONS_TIMEOUT_MS = 5000;

/**
 * Async, fetches on cache miss. For non-React callers that CAN await —
 * currently `cartStore.ts`, deciding which MFL competitions a completed order
 * may enter. A cache miss there must fetch, not silently yield [].
 *
 * The guard is applied here, not at the call sites, so a future caller inherits
 * it without having to know. It is `promotionsEnabled` — the same predicate the
 * query options use for `enabled` — because `queryClient.ensureQueryData` /
 * `fetchQuery` ignore `enabled` entirely, confirmed against
 * @tanstack/query-core 5.100.9 (`ensureQueryData` calls `fetchQuery`
 * unconditionally on a cache miss; `enabled` is a `QueryObserver`-only
 * concept). Sharing the predicate is what stops the imperative path and the
 * hook path from drifting apart; see `promotionsQueryOptions.ts` for why each
 * of its two clauses exists. Without it, a cache miss with no store selected —
 * or, on the gift card store, EVERY completed order, because that cache entry
 * is permanently cold by design — would take a guaranteed 400 on a payment
 * path. The call counts are the assertion; the empty return value alone would
 * pass either way.
 */
export const ensurePromotions = async (rootStore: RootStore): Promise<DerivedPromotions> => {
  const storeNumber = rootStore.shopSelectionStore.selectedStore?.store_number;
  if (!promotionsEnabled(storeNumber)) {
    return EMPTY_DERIVED_PROMOTIONS;
  }

  const options = optionsForSelectedStore(rootStore);
  const arms = await withTimeout<PromotionArms | undefined>(
    queryClient.ensureQueryData(options),
    ENSURE_PROMOTIONS_TIMEOUT_MS,
    () => {
      logger.error('promotions', 'Timed out loading promotions, continuing without them', {
        storeNumber,
        timeoutMs: ENSURE_PROMOTIONS_TIMEOUT_MS,
      });
      return undefined;
    },
    // Last-resort value if the logger itself throws inside the timer callback.
    // `undefined` takes the same path as a timeout, so a broken logger cannot
    // reintroduce the unbounded wait on the post-payment path.
    undefined,
  );
  if (!arms) {
    return EMPTY_DERIVED_PROMOTIONS;
  }
  return derivePromotions(arms, promotionsGateFromRootStore(rootStore));
};
